import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import '@aws-amplify/ui-react/styles.css';
import './lib/amplify';
import { useAuthStore } from './store/authStore';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Admin } from './pages/Admin';
import { History } from './pages/History';
import { UserRole, Country } from './types';
import { Loader2 } from 'lucide-react';

// Custom Authenticator components
const components = {
  Header() {
    const { t } = useTranslation();
    return (
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">AlmorzApp</h1>
        <p className="text-muted-foreground">{t('nav.home')} · Cloudhesive</p>
      </div>
    );
  },
};

const formFields = {
  signIn: {
    username: {
      label: 'Email',
      placeholder: 'Ingresa tu email',
      isRequired: true,
    },
    password: {
      label: 'Contraseña',
      placeholder: 'Ingresa tu contraseña',
      isRequired: true,
    },
  },
};

function AuthenticatedApp() {
  const { user: authUser } = useAuthenticator((context) => [context.user]);
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (authUser) {
          // Extract user data from Cognito
          const userId = authUser.username;
          const email = authUser.signInDetails?.loginId || '';
          const name =
            authUser.signInDetails?.loginId?.split('@')[0] || authUser.username;

          // Get groups and custom attributes from token using fetchAuthSession
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];

          // Get country from custom attribute in ID token
          const country = idToken?.payload['custom:PAIS'] as Country;

          if (!country || !['ARG', 'CHL', 'COL'].includes(country)) {
            console.error('Invalid or missing country attribute');
            setUser(null);
            setLoading(false);
            return;
          }

          // Determine role and admin country based on country-specific admin groups
          let role: UserRole = 'USER';
          let adminCountry: Country | undefined = undefined;
          let effectiveCountry: Country = country;

          if (Array.isArray(groups)) {
            const countryAdminGroup = `ADMIN_${country}`;
            if (groups.includes(countryAdminGroup) || groups.includes('almorzapp-system-access-admin')) {
              role = 'ADMIN';
            }

            // Determine admin country from group membership
            if (groups.includes('almorzapp-system-access-admin-arg')) {
              adminCountry = 'ARG';
            } else if (groups.includes('almorzapp-system-access-admin-chl')) {
              adminCountry = 'CHL';
            } else if (groups.includes('almorzapp-system-access-admin-col')) {
              adminCountry = 'COL';
            }

            // For ADMIN users, use adminCountry as the effective country if available
            if (role === 'ADMIN' && adminCountry) {
              effectiveCountry = adminCountry;
            }
          }

          console.log('[App] User authentication data:', {
            email,
            role,
            originalCountry: country,
            adminCountry,
            effectiveCountry,
            groups,
          });

          setUser({
            userId,
            email,
            name,
            role,
            country: effectiveCountry, // This is what components will use for filtering
            originalCountry: country, // Original custom:PAIS attribute
            adminCountry, // Keep this for display purposes
          });
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [authUser, setUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <p className="text-destructive font-medium">No tienes acceso a esta aplicación</p>
          <p className="text-muted-foreground text-sm mt-2">
            Contacta a un administrador para obtener acceso
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/my-order" element={<Dashboard />} />
          <Route path="/history" element={<History />} />

          {/* Admin routes */}
          {user.role === 'ADMIN' && (
            <>
              <Route path="/admin" element={<Admin />} />
            </>
          )}

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

function App() {
  return (
    <Authenticator
      hideSignUp={true}
      components={components}
      formFields={formFields}
    >
      {() => <AuthenticatedApp />}
    </Authenticator>
  );
}

export default App;
